import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { login as apiLogin, me } from '@/api';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/auth';
import LanguageSelector from '@/components/LanguageSelector';
import { Mail, Lock, Loader2, ArrowLeft } from 'lucide-react';

const LOGO_URL = '/logo.png';

export default function Login(){
  const { t } = useLanguage();
  const { login: authLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      localStorage.clear();
      sessionStorage.clear();
      localStorage.setItem("servicego_app_version", "2.0.0");
      
      const { data } = await apiLogin(email, password);
      sessionStorage.setItem('jc_token', data.access_token);
      
      const meRes = await me();
      const userData = meRes.data;
      
      authLogin(data.access_token, userData);
      
      toast.success(t('auth.loginSuccess'));
      
      const r = userData.role;
      if (r === 'client') nav('/cliente/dashboard');
      else if (r === 'company') nav('/ditta/dashboard');
      else if (r === 'admin') nav('/admin');
      else nav('/');
    } catch (err) {
      console.error('Login error:', err);
      toast.error(t('auth.invalidCredentials'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      <Toaster />
      
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <ArrowLeft className="w-5 h-5 text-slate-500" />
              <img src={LOGO_URL} alt="ServiceGo" className="h-10 w-auto" />
            </Link>
            <LanguageSelector />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex items-center justify-center px-4 py-12 sm:py-20">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-8 sm:p-10">
            <div className="text-center mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                {t('auth.welcomeBack')}
              </h1>
              <p className="text-slate-500 mt-2">
                {t('auth.loginSubtitle')}
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input 
                    data-testid="login-email"
                    type="email"
                    placeholder="email@esempio.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="h-12 pl-12 rounded-xl border-slate-200 focus:border-[#5848FF] focus:ring-[#5848FF]"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input 
                    data-testid="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="h-12 pl-12 rounded-xl border-slate-200 focus:border-[#5848FF] focus:ring-[#5848FF]"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-[#5848FF] hover:underline"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              <Button 
                data-testid="login-submit"
                type="submit" 
                disabled={isLoading}
                className="w-full h-12 rounded-xl bg-[#5848FF] hover:bg-[#4638EF] text-white font-semibold text-base shadow-lg shadow-indigo-200"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  t('auth.login')
                )}
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-center text-slate-600 mb-4">
                {t('auth.noAccount')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  variant="outline"
                  onClick={() => nav('/cliente/registrazione')}
                  className="h-11 rounded-xl border-slate-200 hover:border-[#5848FF] hover:bg-indigo-50"
                >
                  Privato
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => nav('/ditta/registrazione')}
                  className="h-11 rounded-xl border-slate-200 hover:border-emerald-500 hover:bg-emerald-50"
                >
                  Ditta
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
